import { type TFunction } from 'i18next';

import {
  type TaxCodeFilterOptionsResponse,
  type TaxCodesQueryParams,
  zodTaxCodesQueryParamsSchema,
} from '~/data/taxCode';

import {
  createPersistableQueryFilterKeyGuard,
  type FilterDraft,
  UNIVERSAL_FILTER_TYPES,
  type UniversalFiltersPageConfig,
} from '~/modules/universalFilters';

const isPersistableTaxCodesFilterKey =
  createPersistableQueryFilterKeyGuard<TaxCodesQueryParams>({
    queryKeys: zodTaxCodesQueryParamsSchema.keyof().options,
  });

export function getTaxCodesFilterConfig(
  t: TFunction<'taxCodes'>,
  filterOptions: TaxCodeFilterOptionsResponse,
): UniversalFiltersPageConfig<TaxCodesQueryParams> {
  return {
    drawerFields: [
      {
        field: 'companyIds',
        input: 'companies',
        isMultiSelect: true,
        label: t(($) => $.drawer.companyIds.label),
        placeholder: t(($) => $.drawer.companyIds.placeholder),
      },
      {
        field: 'names',
        input: 'multiSelect',
        label: t(($) => $.drawer.names.label),
        options: filterOptions.names.map((name) => ({
          label: name,
          value: name,
        })),
        placeholder: t(($) => $.drawer.names.placeholder),
      },
      {
        field: 'rates',
        input: 'multiSelect',
        label: t(($) => $.drawer.rates.label),
        options: filterOptions.rates.map((rate) => ({
          label: rate,
          value: rate,
        })),
        placeholder: t(($) => $.drawer.rates.placeholder),
      },
      {
        field: 'codes',
        input: 'multiSelect',
        label: t(($) => $.drawer.codes.label),
        options: filterOptions.codes.map((code) => ({
          label: code,
          value: code,
        })),
        placeholder: t(($) => $.drawer.codes.placeholder),
      },
      {
        field: 'isReverseCharge',
        input: 'booleanToggle',
        label: t(($) => $.drawer.isReverseCharge.label),
      },
    ],
    mapToPayload: ({ filters, search }) =>
      mapTaxCodesFiltersToPayload(filters, search),
    savedFilters: {
      enabled: true,
    },
    search: {
      placeholder: t(($) => $.filtersRow.search.placeholder),
    },
    type: UNIVERSAL_FILTER_TYPES.taxCodes,
  };
}

export function mapTaxCodesFiltersToPayload(
  filters: FilterDraft,
  search: string,
): Partial<TaxCodesQueryParams> {
  const payload: Partial<TaxCodesQueryParams> = {};

  for (const filter of filters) {
    if (isPersistableTaxCodesFilterKey(filter.field)) {
      Object.assign(payload, { [filter.field]: filter.value });
    }
  }

  const trimmedSearch = search.trim();
  if (trimmedSearch) payload.searchString = trimmedSearch;

  return payload;
}
